/** ابزارهای مشترکِ «داشبوردِ قراردادها» برای ناظر و مشاور */

const FA_DIGITS = { '۰': '0', '۱': '1', '۲': '2', '۳': '3', '۴': '4', '۵': '5', '۶': '6', '۷': '7', '۸': '8', '۹': '9' }

/** یکسان‌سازیِ متن: ارقام فارسی → لاتین، حذفِ نیم‌فاصله و فاصله‌های اضافه */
export const normalizeFa = (text) =>
  String(text || '')
    .replace(/[۰-۹]/g, (d) => FA_DIGITS[d] || d)
    .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
    .replace(/[\u200c\u200e]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

/** متنِ جستجو ⇒ واژه‌ها (همه با AND باید پیدا شوند) */
export const tokenize = (query) => {
  const t = normalizeFa(query)
  if (!t) return []
  return t.split(' ').filter(Boolean)
}

/** نامِ پیمانکارِ یک ردیف (رشته یا شیء) */
export const contractorName = (row) => {
  const c = row?.contractor
  if (!c) return ''
  if (typeof c === 'string') return c
  return c.name || ''
}

/** نامِ سازمانِ طرف قرارداد */
export const contracteeName = (row) => {
  const c = row?.contractee
  if (!c) return ''
  if (typeof c === 'string') return c
  return c.name || ''
}

/** آیا ردیف با واژه‌های جستجو می‌خواند؟ (همهٔ واژه‌ها با AND) */
export const rowMatches = (row, tokens) => {
  if (!tokens.length) return true
  const haystack = normalizeFa(
    [
      row?.number,
      row?.subject,
      contractorName(row),
      contracteeName(row),
      row?.consultant?.name,
      row?.supervisor?.name,
    ].filter(Boolean).join(' ')
  ).toLowerCase()
  return tokens.every((t) => haystack.indexOf(t.toLowerCase()) !== -1)
}

/**
 * مرتب‌سازیِ لیستِ قراردادهای ناظر/مشاور:
 *   name : اول به ترتیبِ پیمانکار، سپس در قراردادهای هر پیمانکار به ترتیبِ
 *          نزولیِ تاریخِ قرارداد  (پیش‌فرض)
 *   sub  : پیمانکارانِ مشترک در بالا، سپس به ترتیبِ پیمانکار و تاریخِ نزولی
 *   date : فقط تاریخِ نزولیِ قرارداد
 */
export const sortRows = (rows, mode = 'name') => {
  const list = [...(rows || [])]
  const byDateDesc = (a, b) => {
    const da = new Date(a?.date || 0).getTime()
    const db = new Date(b?.date || 0).getTime()
    if (da !== db) return db - da
    return (b?.id || 0) - (a?.id || 0)
  }
  const byContractor = (a, b) => {
    const na = contractorName(a)
    const nb = contractorName(b)
    if (na !== nb) return na.localeCompare(nb, 'fa')
    return 0
  }
  if (mode === 'date') return list.sort(byDateDesc)
  if (mode === 'sub') {
    return list.sort((a, b) => {
      const sa = a?.contractor_subscribed === true ? 1 : 0
      const sb = b?.contractor_subscribed === true ? 1 : 0
      if (sa !== sb) return sb - sa
      const c = byContractor(a, b)
      if (c !== 0) return c
      return byDateDesc(a, b)
    })
  }
  // پیش‌فرض: پیمانکار ⇒ سپس تاریخِ نزولی
  return list.sort((a, b) => {
    const c = byContractor(a, b)
    if (c !== 0) return c
    return byDateDesc(a, b)
  })
}

/** کلیدِ یکتای پیمانکار در یک ردیف (برای شمارشِ پیمانکاران) */
const contractorKey = (row) => {
  const c = row?.contractor
  if (c && typeof c === 'object' && c.id) return 'id:' + c.id
  const name = contractorName(row)
  return name ? 'name:' + name : 'row:' + (row?.id || '')
}

/**
 * گروه‌بندیِ قراردادها به تفکیکِ پیمانکار:
 *   mode 'name' : گروه‌ها به ترتیبِ نامِ پیمانکار (پیش‌فرض)
 *   mode 'sub'  : پیمانکارانِ مشترک اول، سپس بقیه؛ همه به ترتیبِ نام
 *   mode 'date' : گروه‌بندی نمی‌شود (فقط ترتیبِ نزولیِ تاریخ)
 * داخلِ هر گروه همیشه تاریخِ نزولی.
 * خروجی: [{ key, name, rows, count, subscribed }]
 */
export const groupByContractor = (rows, mode = 'name') => {
  const map = new Map()
  ;(rows || []).forEach((row) => {
    const key = contractorKey(row)
    if (!map.has(key)) {
      map.set(key, { key, name: contractorName(row) || '—', rows: [], subscribed: false })
    }
    const g = map.get(key)
    g.rows.push(row)
    if (row?.contractor_subscribed === true) g.subscribed = true
  })
  const groups = [...map.values()]
  groups.forEach((g) => {
    g.count = g.rows.length
    g.rows.sort((a, b) => {
      const da = new Date(a?.date || 0).getTime()
      const db = new Date(b?.date || 0).getTime()
      if (da !== db) return db - da
      return (b?.id || 0) - (a?.id || 0)
    })
  })
  groups.sort((a, b) => {
    if (mode === 'sub' && a.subscribed !== b.subscribed) return a.subscribed ? -1 : 1
    return a.name.localeCompare(b.name, 'fa')
  })
  return groups
}

/** آمارِ داشبورد: تعداد قرارداد، تعداد پیمانکار، مشترک و غیرمشترک */
export const computeStats = (rows) => {
  const list = rows || []
  const contractors = new Map()
  let subContracts = 0
  let nonSubContracts = 0
  list.forEach((row) => {
    const key = contractorKey(row)
    const isSub = row?.contractor_subscribed === true
    if (isSub) subContracts += 1
    else nonSubContracts += 1
    if (!contractors.has(key)) {
      contractors.set(key, { key, subscribed: isSub })
    } else if (isSub) {
      contractors.get(key).subscribed = true
    }
  })
  const all = [...contractors.values()]
  return {
    contracts: list.length,
    contractors: all.length,
    subscribed: all.filter((c) => c.subscribed).length,
    nonSubscribed: all.filter((c) => !c.subscribed).length,
    subContracts,
    nonSubContracts,
  }
}

/**
 * فیلترِ لیست بر اساسِ کارتِ انتخاب‌شده و متنِ جستجو.
 * mode: 'all' | 'subscribed' | 'non-subscribed'
 */
export const filterRows = (rows, { query = '', mode = 'all' } = {}) => {
  const tokens = tokenize(query)
  return (rows || []).filter((row) => {
    if (mode === 'subscribed' && row?.contractor_subscribed !== true) return false
    if (mode === 'non-subscribed' && row?.contractor_subscribed === true) return false
    return rowMatches(row, tokens)
  })
}
